import React, { useEffect, useState, useRef, useCallback } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, createTheme, CssBaseline, Snackbar, Alert, GlobalStyles } from '@mui/material';
import { getCookie } from './utils/cookies';
import { silentRefresh, forceLogout } from './api/axiosConfig';

// Centralized routing
import AppRoutes from './routes/AppRoutes';

// Proactive refresh interval: 20 minutes (in milliseconds)
const REFRESH_INTERVAL_MS = 20 * 60 * 1000;
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes

import TablePaginationActions from './components/common/TablePaginationActions';

// MUI Theme Config (Modern, standardized data table design system)
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#4146e0',
            dark: '#3539bc',
            light: '#7a8cf4',
        },
        secondary: {
            main: '#4146e0',
            dark: '#3539bc',
            light: '#7a8cf4',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
        text: {
            primary: '#101828',
            secondary: '#475467',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: { fontWeight: 800 },
        h2: { fontWeight: 800 },
        h3: { fontWeight: 700 },
        h4: { fontWeight: 700 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    padding: '7px 16px',
                    minHeight: 38,
                    boxShadow: 'none',
                    fontFamily: '"Inter", sans-serif',
                },
                containedPrimary: {
                    backgroundColor: '#4146e0',
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: '#3539bc',
                        boxShadow: 'none',
                    },
                },
                containedSecondary: {
                    backgroundColor: '#4146e0',
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: '#3539bc',
                        boxShadow: 'none',
                    },
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                outlined: {
                    backgroundColor: '#ffffff',
                    borderColor: '#d0d5dd',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    color: '#344054',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#f8fafc',
                        borderColor: '#cbd5e1',
                        color: '#1e293b',
                    },
                    '& .MuiButton-startIcon': {
                        color: '#475467',
                    },
                    '& .MuiButton-endIcon': {
                        color: '#475467',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#ffffff',
                        borderColor: '#e2e8f0',
                        color: '#94a3b8',
                        '& .MuiButton-startIcon': {
                            color: '#94a3b8',
                        },
                    },
                },
                outlinedSecondary: {
                    backgroundColor: '#ffffff',
                    borderColor: '#d0d5dd',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    color: '#344054',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: '#f8fafc',
                        borderColor: '#cbd5e1',
                        color: '#1e293b',
                    },
                    '& .MuiButton-startIcon': {
                        color: '#475467',
                    },
                    '& .MuiButton-endIcon': {
                        color: '#475467',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#ffffff',
                        borderColor: '#e2e8f0',
                        color: '#94a3b8',
                        '& .MuiButton-startIcon': {
                            color: '#94a3b8',
                        },
                    },
                },
                outlinedPrimary: {
                    backgroundColor: '#ffffff',
                    borderColor: '#4146e0',
                    color: '#4146e0',
                    fontWeight: 600,
                    '&:hover': {
                        backgroundColor: 'rgba(65, 70, 224, 0.04)',
                        borderColor: '#3539bc',
                    },
                    '& .MuiButton-startIcon': {
                        color: '#4146e0',
                    },
                    '& .MuiButton-endIcon': {
                        color: '#4146e0',
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: '#ffffff',
                    fontSize: '0.875rem',
                    color: '#314154',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#edf1f6',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d0d5dd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4146e0',
                        borderWidth: '1.5px',
                    },
                },
                input: {
                    padding: '8.5px 14px',
                    '&::placeholder': {
                        color: '#98a0aa',
                        opacity: 1,
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: '#ffffff',
                },
                select: {
                    paddingTop: '8.5px',
                    paddingBottom: '8.5px',
                    color: '#314154',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                },
                icon: {
                    color: '#98a0aa',
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    color: '#64748b',
                    '&:hover': {
                        backgroundColor: '#f8fafc',
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    '& .MuiSvgIcon-root': {
                        color: '#98a0aa',
                        fontSize: '1.125rem',
                    },
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                variant: 'scrollable',
                scrollButtons: 'auto',
                allowScrollButtonsMobile: true,
            },
            styleOverrides: {
                root: {
                    minHeight: 44,
                    borderBottom: '1px solid #eaecf0',
                },
                indicator: {
                    backgroundColor: '#4146e0',
                    height: 2.5,
                    borderRadius: '2px 2px 0 0',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minWidth: 'auto',
                    minHeight: 44,
                    padding: '10px 20px',
                    color: '#64748b',
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    '&.Mui-selected': {
                        color: '#4146e0',
                        fontWeight: 600,
                    },
                    '&:hover': {
                        color: '#1e293b',
                    },
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: '1px solid #eaecf0',
                    boxShadow: '0 1px 3px 0 rgba(16, 24, 40, 0.05), 0 1px 2px 0 rgba(16, 24, 40, 0.03)',
                    backgroundColor: '#ffffff',
                    overflowX: 'auto',
                    '&:has(tbody tr:only-child td[colspan]) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                    '&:has(tbody:empty) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    backgroundColor: '#f8fafc',
                    '& .MuiTableCell-head': {
                        backgroundColor: '#f8fafc',
                        color: '#475467',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        letterSpacing: '0.02em',
                        textTransform: 'none',
                        borderBottom: '1px solid #eaecf0',
                        padding: '12px 16px',
                        whiteSpace: 'nowrap',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderColor: '#f2f4f7',
                    padding: '14px 16px',
                    fontSize: '0.875rem',
                    color: '#1e293b',
                    verticalAlign: 'middle',
                },
                head: {
                    backgroundColor: '#f8fafc',
                    color: '#475467',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    borderBottom: '1px solid #eaecf0',
                    padding: '12px 16px',
                    whiteSpace: 'nowrap',
                    '& b, & strong': {
                        fontWeight: 600,
                        color: '#475467',
                    },
                },
                body: {
                    fontSize: '0.875rem',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    transition: 'background-color 0.15s ease',
                    '&:hover': {
                        backgroundColor: '#f8fafc !important',
                    },
                    '&.Mui-selected': {
                        backgroundColor: 'rgba(79, 70, 229, 0.04) !important',
                        '&:hover': {
                            backgroundColor: 'rgba(79, 70, 229, 0.07) !important',
                        },
                    },
                },
            },
        },
        MuiTablePagination: {
            defaultProps: {
                ActionsComponent: TablePaginationActions,
                labelDisplayedRows: ({ from, to, count }) =>
                    `Showing ${from}-${to} of ${count !== -1 ? count : `more than ${to}`} items`,
            },
            styleOverrides: {
                root: {
                    borderTop: '1px solid #eaecf0',
                    color: '#475467',
                    backgroundColor: '#ffffff',
                },
                toolbar: {
                    minHeight: 56,
                    paddingLeft: 20,
                    paddingRight: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 16,
                },
                spacer: {
                    display: 'none',
                },
                displayedRows: {
                    color: '#475467',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    marginRight: 'auto',
                    order: -1,
                },
                selectLabel: {
                    color: '#475467',
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                },
                select: {
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#344054',
                    borderRadius: 6,
                    border: '1px solid #eaecf0',
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
    },
});

function App() {
    const dispatch = useDispatch();
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const refreshIntervalRef = useRef(null);

    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const tenantId = useSelector((state) => state.auth.user?.tenantId);
    // Cookie-based check combined with Redux state for reactivity
    const hasToken = isAuthenticated || !!getCookie('adminToken');

    useEffect(() => {
        const handleToast = (e) => {
            setToast({
                open: true,
                message: e.detail.message,
                severity: e.detail.severity || 'info'
            });
        };
        window.addEventListener('app-toast', handleToast);
        return () => window.removeEventListener('app-toast', handleToast);
    }, []);

    useEffect(() => {
        if (hasToken) {
            dispatch.auth.fetchMeAsync();
        }
    }, [dispatch, hasToken]);

    // ─── Frontend Inactivity Tracker ────────────────────────────────────────
    useEffect(() => {
        if (!hasToken) return;

        const updateActivity = () => {
            localStorage.setItem('lastActivityTime', Date.now().toString());
        };

        // Initialize on mount
        updateActivity();

        window.addEventListener('mousemove', updateActivity);
        window.addEventListener('keydown', updateActivity);
        window.addEventListener('click', updateActivity);
        window.addEventListener('scroll', updateActivity);

        const inactivityInterval = setInterval(() => {
            const lastActivityStr = localStorage.getItem('lastActivityTime');
            const lastActivity = lastActivityStr ? parseInt(lastActivityStr, 10) : Date.now();
            
            if (Date.now() - lastActivity > INACTIVITY_LIMIT_MS) {
                clearInterval(inactivityInterval);
                forceLogout('You have been logged out due to 30 minutes of inactivity.');
            }
        }, 60 * 1000); // Check every minute

        return () => {
            window.removeEventListener('mousemove', updateActivity);
            window.removeEventListener('keydown', updateActivity);
            window.removeEventListener('click', updateActivity);
            window.removeEventListener('scroll', updateActivity);
            clearInterval(inactivityInterval);
        };
    }, [hasToken]);

    // ─── Proactive Background Token Refresh ─────────────────────────────────
    // Runs silentRefresh every 20 minutes to keep the refresh token rotated
    // before the backend's RTR window expires. Only runs when authenticated.
    const startRefreshInterval = useCallback(() => {
        // Clear any existing interval first
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
        }

        refreshIntervalRef.current = setInterval(async () => {
            const hasRefreshToken = !!getCookie('adminRefreshToken');
            if (!hasRefreshToken) {
                // User logged out or token cleared — stop the interval
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
                return;
            }

            const success = await silentRefresh();
            if (!success) {
                console.warn('[App] Proactive token refresh failed. Will retry on next interval.');
            }
        }, REFRESH_INTERVAL_MS);
    }, []);

    useEffect(() => {
        if (hasToken) {
            startRefreshInterval();
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
    }, [hasToken, tenantId, startRefreshInterval]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <GlobalStyles
                styles={{
                    'input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                    },
                    'input[type=number]': {
                        MozAppearance: 'textfield',
                    },
                    ':is(.MuiTableContainer-root, .MuiPaper-root, .MuiCard-root, .MuiBox-root):has(tbody tr:only-child td[colspan]) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                    ':is(.MuiTableContainer-root, .MuiPaper-root, .MuiCard-root, .MuiBox-root):has(tbody:empty) .MuiTablePagination-root': {
                        display: 'none !important',
                    },
                }}
            />
            <BrowserRouter>
                <AppRoutes />
            </BrowserRouter>
            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setToast(prev => ({ ...prev, open: false }))} severity={toast.severity} sx={{ width: '100%' }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </ThemeProvider>
    );
}

export default App;
